


import { Component, Ref, Vue } from "vue-property-decorator";

import { Filter } from "@Internal/Contracts";
import { Payments, Records } from "App";
import { EventBus } from "App/IOC";

import RecordsComponent from "Web/Components/Records.vue";

import {
    Column,
    CustomNotification,
    Invoice,
    PagerSorter
} from "App/Models";

@Component( {
    components: {
        Records: RecordsComponent
    }
})
export default class PaymentsView extends Vue {
    @Ref( "records" )
    private readonly $records!: RecordsComponent;

    private columns: Column[] = Payments.Columns.Get();
    private records: Invoice[] = [];

    /* Invoices are paged server-side from the local mirror (see backend Payments service): we pass
       the filters + pager straight through. The total is exact, so a real "jump to page N" pager
       works; the header count stays hidden to match the Facturation mockup. */
    private pagerSorter: PagerSorter = new PagerSorter( 10 );

    private filters = Payments.Filters.Get;

    private async update(
        filters: Filter[],
        pagerSorter: PagerSorter
    ): Promise<void> {
        await this.$nextTick();
        this.$records.Loading();

        const [ records, updatedPagerSorter, error ] = await Payments.Find( filters, pagerSorter );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.records = records;
        this.pagerSorter = updatedPagerSorter;
    }

    private formatTotal( invoice: Invoice ): string {
        if( invoice.Total === undefined )
            return "—";

        /* Invoices are billed amounts: the Stripe total already includes VAT, so flag them TTC. */
        return `${ invoice.Total.ToFixed( 2 ) } €`
            + ` ${ this.$t( "GLOBAL_includingTaxSuffix" ) }`;
    }

    private frequencyLabel( invoice: Invoice ): string {
        if( invoice.Frequency === "Monthly" )
            return this.$t( "PAYMENTS_frequencyMonthly" ) as string;

        if( invoice.Frequency === "Yearly" )
            return this.$t( "PAYMENTS_frequencyYearly" ) as string;

        return "—";
    }

    /* CustomDate renders to a localized string through its toString; fall back to a dash. */
    private formatDate( date?: { toString(): string } ): string {
        return date !== undefined ? date.toString() : "—";
    }

    private statusLabel( invoice: Invoice ): string {
        switch( invoice.Status ) {
            case "Paid":
                return this.$t( "SUBSCRIPTION_paid" ) as string;

            case "Open":
                return this.$t( "SUBSCRIPTION_open" ) as string;

            case "Uncollectible":
                return this.$t( "SUBSCRIPTION_uncollectible" ) as string;

            case "Rejected":
                return this.$t( "CUSTOMERS_invoiceRejected" ) as string;

            default:
                return this.$t( "SUBSCRIPTION_void" ) as string;
        }
    }

    private statusClass( invoice: Invoice ): string {
        return `v-payments__status--${ ( invoice.Status || "" ).toLowerCase() }`;
    }

    /* Row click opens the related customer. The invoice carries the internal customer id only when
       the Stripe customer was mapped at mirror time; otherwise there is nothing to open, so we warn
       instead. The navigation list lets the customer detail page page through the other invoices'
       customers with the prev/next arrows. */
    private executeAction( id: string, rows: Invoice[] ): void {
        switch( id ) {
            case "open": {
                const invoice = rows[ 0 ];

                if( invoice.Customer === undefined ) {
                    EventBus.Publish( "Notification", CustomNotification.Failure(
                        this.$t( "PAYMENTS_title" ).toString(),
                        this.$t( "PAYMENTS_noCustomerLinked" ).toString()
                    ));

                    break;
                }

                const linked = this.records.filter(
                    ( record ): boolean => record.Customer !== undefined
                );

                const urls = linked.map( ( record ): string => `/customers/${ record.Customer }` );
                const index = linked.findIndex( ( record ): boolean => record.Id === invoice.Id );

                Records.Navigation.SetList( urls, index );

                this.$router.push( `/customers/${ invoice.Customer }` );

                break;
            }

            default:
                EventBus.Publish( "UnderConstruction" );
                break;
        }
    }
}
